/**
 * Shared visual config + tiny date utilities for the maintenance history views.
 */

export const EVENT_TYPE_META = {
  maintenance_log: {
    label: 'Maintenance Log',
    short: 'Log',
    chipClass: 'bg-sky-500',
    cellClass: 'bg-sky-50 text-sky-900 border-sky-200',
    dotColor: '#0ea5e9', // sky-500
  },
  completed_work_order: {
    label: 'Completed Work Order',
    short: 'WO done',
    chipClass: 'bg-emerald-500',
    cellClass: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    dotColor: '#10b981', // emerald-500
  },
  open_work_order: {
    label: 'Open Work Order',
    short: 'WO due',
    chipClass: 'bg-amber-500',
    cellClass: 'bg-amber-50 text-amber-900 border-amber-200',
    dotColor: '#f59e0b', // amber-500
  },
  completed_request: {
    label: 'Completed Request',
    short: 'Req done',
    chipClass: 'bg-violet-500',
    cellClass: 'bg-violet-50 text-violet-900 border-violet-200',
    dotColor: '#8b5cf6', // violet-500
  },
};

export const EVENT_TYPE_ORDER = [
  'maintenance_log',
  'completed_work_order',
  'open_work_order',
  'completed_request',
];

/** Format an ISO date string (`YYYY-MM-DD`) as a human-readable label. */
export function formatDayLabel(iso) {
  if (!iso) return '';
  // Construct in local time so '2026-05-15' stays 'May 15' instead of jumping zones.
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return dt.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Format an ISO datetime back to a 'Mon DD, YYYY' label. */
export function formatLongDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** YYYY-MM-DD for a JS Date in local time. */
export function isoDay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Bucket a calendar-events row by LOCAL day.
 *
 * Prefers the raw `event_date` ISO (which carries the original timezone) and
 * converts it to a `YYYY-MM-DD` string in the browser's local timezone. Falls
 * back to the pre-bucketed `date` field for older payloads.
 *
 * Careful: JS `new Date('2026-08-10')` parses a bare `YYYY-MM-DD` as UTC
 * midnight, which shifts backward one day in every negative-offset timezone
 * (America/New_York would render Aug 9). For bare-date inputs we bypass Date
 * parsing entirely and treat the string as an already-local calendar date.
 *
 * Why: a log timestamped `2026-08-11T02:00:00Z` displays as "Aug 10" in the
 * List view (which uses `toLocaleDateString`) but was pre-bucketed on the
 * backend as `2026-08-11` (UTC calendar slice). Recomputing here keeps the
 * two views in sync.
 */
const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function localBucketDay(event) {
  const raw = event?.event_date;
  if (typeof raw === 'string' && raw) {
    // Bare `YYYY-MM-DD` — already a calendar date, no timezone shift.
    if (BARE_DATE_RE.test(raw)) return raw;
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) return isoDay(dt);
  }
  return event?.date || null;
}

/** Number of days between two ISO `YYYY-MM-DD` strings (b - a). */
export function dayDiff(aIso, bIso) {
  const [ay, am, ad] = aIso.split('-').map(Number);
  const [by, bm, bd] = bIso.split('-').map(Number);
  const a = new Date(ay, am - 1, ad).getTime();
  const b = new Date(by, bm - 1, bd).getTime();
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}
